import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowLeft,
  ExternalLink,
  FileText,
  Loader2,
  Save,
  Trash2,
  Upload,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  createInvoice,
  deleteInvoice,
  getInvoice,
  getInvoiceSignedUrl,
  updateInvoice,
} from "@/lib/invoices.functions";
import { listClients } from "@/lib/customers.functions";
import { confirmDialog } from "@/lib/confirm-dialog";

export const Route = createFileRoute("/_admin/admin/invoices/$id")({
  head: () => ({
    meta: [
      { title: "Rechnung bearbeiten – Admin – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: InvoiceEditor,
});

const MAX_BYTES = 20 * 1024 * 1024; // 20 MB
const CURRENCIES = ["EUR", "CHF", "USD"] as const;

function formatBytes(n: number | null | undefined): string {
  if (!n || n <= 0) return "–";
  const units = ["B", "KB", "MB", "GB"];
  let val = n;
  let idx = 0;
  while (val >= 1024 && idx < units.length - 1) {
    val /= 1024;
    idx++;
  }
  return `${val.toFixed(val >= 10 || idx === 0 ? 0 : 1)} ${units[idx]}`;
}

/** Parst deutsche Betragsangaben ("1.234,56") in Cent. */
function parseAmountToCents(input: string): number | null {
  const s = input.trim();
  if (!s) return null;
  // Punkt als Tausendertrenner entfernen, Komma → Punkt.
  const normalized = s.replace(/\./g, "").replace(",", ".");
  const n = Number(normalized);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

function formatCentsForInput(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function todayIso(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

function InvoiceEditor() {
  const { id } = useParams({ from: "/_admin/admin/invoices/$id" });
  const isNew = id === "new";
  const navigate = useNavigate();
  const qc = useQueryClient();

  const getFn = useServerFn(getInvoice);
  const createFn = useServerFn(createInvoice);
  const updateFn = useServerFn(updateInvoice);
  const deleteFn = useServerFn(deleteInvoice);
  const signFn = useServerFn(getInvoiceSignedUrl);
  const clientsFn = useServerFn(listClients);

  const { data: existing, isLoading } = useQuery({
    queryKey: ["admin-invoice", id],
    queryFn: () => getFn({ data: { id } }),
    enabled: !isNew,
  });
  const { data: clients } = useQuery({
    queryKey: ["admin-clients-list-slim"],
    queryFn: () => clientsFn(),
  });

  const [clientId, setClientId] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(todayIso());
  const [amountInput, setAmountInput] = useState("");
  const [currency, setCurrency] = useState<string>("EUR");
  const [visible, setVisible] = useState(true);
  const [notes, setNotes] = useState("");
  const [pdf, setPdf] = useState<{
    path: string;
    size: number | null;
    mime: string | null;
  } | null>(null);

  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const initialised = useRef(false);

  useEffect(() => {
    if (!existing || initialised.current) return;
    initialised.current = true;
    setClientId(existing.client_id);
    setInvoiceNumber(existing.invoice_number);
    setInvoiceDate(existing.invoice_date);
    setAmountInput(formatCentsForInput(existing.amount_gross_cents));
    setCurrency(existing.currency);
    setVisible(existing.visible);
    setNotes(existing.notes ?? "");
    setPdf({
      path: existing.pdf_path,
      size: existing.pdf_size_bytes,
      mime: existing.pdf_mime_type,
    });
  }, [existing]);

  const activeClients = useMemo(
    () => (clients ?? []).filter((c) => c.status === "active"),
    [clients],
  );

  async function handleUpload(f: File) {
    setError(null);
    if (f.type && f.type !== "application/pdf") {
      setError("Nur PDF-Dateien erlaubt.");
      return;
    }
    if (f.size > MAX_BYTES) {
      setError(`Datei ist zu groß (max. ${formatBytes(MAX_BYTES)}).`);
      return;
    }
    if (!clientId) {
      setError("Bitte zuerst einen Kunden auswählen.");
      return;
    }
    setUploading(true);
    try {
      const uid =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : Math.random().toString(36).slice(2);
      const path = `client-${clientId}/${uid}.pdf`;
      const { error: upErr } = await supabase.storage
        .from("invoices")
        .upload(path, f, { contentType: "application/pdf" });
      if (upErr) throw new Error(upErr.message);
      setPdf({ path, size: f.size, mime: "application/pdf" });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  async function onPreview() {
    if (!pdf || isNew) return;
    try {
      const res = await signFn({ data: { id } });
      window.open(res.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const amountCents = parseAmountToCents(amountInput);
  const canSave =
    !!clientId &&
    !!invoiceNumber.trim() &&
    !!invoiceDate &&
    amountCents !== null &&
    !!pdf;

  async function onSave() {
    setError(null);
    if (!canSave || !pdf || amountCents === null) {
      setError("Bitte alle Pflichtfelder ausfüllen und eine PDF hochladen.");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const res = await createFn({
          data: {
            client_id: clientId,
            invoice_number: invoiceNumber.trim(),
            invoice_date: invoiceDate,
            amount_gross_cents: amountCents,
            currency,
            pdf_path: pdf.path,
            pdf_size_bytes: pdf.size,
            pdf_mime_type: pdf.mime ?? "application/pdf",
            notes: notes.trim() || null,
            visible,
          },
        });
        qc.invalidateQueries({ queryKey: ["admin-invoices"] });
        navigate({ to: "/admin/invoices/$id", params: { id: res.id } });
      } else {
        await updateFn({
          data: {
            id,
            client_id: clientId,
            invoice_number: invoiceNumber.trim(),
            invoice_date: invoiceDate,
            amount_gross_cents: amountCents,
            currency,
            pdf_path: pdf.path,
            pdf_size_bytes: pdf.size,
            pdf_mime_type: pdf.mime ?? "application/pdf",
            notes: notes.trim() || null,
            visible,
          },
        });
        qc.invalidateQueries({ queryKey: ["admin-invoices"] });
        qc.invalidateQueries({ queryKey: ["admin-invoice", id] });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function onDelete() {
    if (isNew) return;
    if (
      !(await confirmDialog({
        title: "Rechnung löschen?",
        description: "Die PDF-Datei wird ebenfalls entfernt.",
      }))
    )
      return;
    try {
      await deleteFn({ data: { id } });
      qc.invalidateQueries({ queryKey: ["admin-invoices"] });
      navigate({ to: "/admin/invoices" });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  if (!isNew && isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Laden …
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      <Link
        to="/admin/invoices"
        className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Zurück zur Übersicht
      </Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-3">
        <h1 className="font-display text-3xl font-extrabold md:text-4xl">
          {isNew ? "Neue Rechnung" : "Rechnung bearbeiten"}
        </h1>
        {!isNew && (
          <button
            type="button"
            onClick={onDelete}
            className="inline-flex items-center gap-1 rounded border border-destructive/40 px-3 py-1.5 text-xs font-semibold text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-3.5 w-3.5" /> Löschen
          </button>
        )}
      </div>

      <div className="mt-8 space-y-6">
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Kunde
          </span>
          <select
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            className="w-full rounded-md border border-input bg-background p-2 text-sm"
          >
            <option value="">– bitte wählen –</option>
            {activeClients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.organization_name}
              </option>
            ))}
          </select>
        </label>

        <div className="grid gap-6 md:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Rechnungsnummer
            </span>
            <input
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              maxLength={60}
              className="w-full rounded-md border border-input bg-background p-2 text-sm"
              placeholder="z. B. 2026-042"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Rechnungsdatum
            </span>
            <input
              type="date"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              className="w-full rounded-md border border-input bg-background p-2 text-sm"
            />
          </label>
        </div>

        <div className="grid gap-6 md:grid-cols-[1fr,120px]">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Betrag brutto
            </span>
            <input
              inputMode="decimal"
              value={amountInput}
              onChange={(e) => setAmountInput(e.target.value)}
              className="w-full rounded-md border border-input bg-background p-2 text-sm tabular-nums"
              placeholder="1.234,56"
            />
            {amountInput && amountCents === null && (
              <p className="mt-1 text-xs text-destructive">
                Betrag konnte nicht gelesen werden – bitte im Format „1.234,56"
                eingeben.
              </p>
            )}
          </label>

          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Währung
            </span>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="w-full rounded-md border border-input bg-background p-2 text-sm"
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div>
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            PDF
          </span>
          {pdf ? (
            <div className="flex flex-wrap items-center gap-3 rounded border border-border bg-secondary/40 p-3 text-sm">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-mono text-xs">{pdf.path}</p>
                <p className="text-[11px] text-muted-foreground">
                  {formatBytes(pdf.size)} · {pdf.mime ?? "application/pdf"}
                </p>
              </div>
              {!isNew && (
                <button
                  type="button"
                  onClick={onPreview}
                  className="inline-flex items-center gap-1 rounded border border-border bg-background px-2 py-1 text-xs font-semibold hover:bg-secondary"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Vorschau
                </button>
              )}
              <button
                type="button"
                onClick={() => fileInput.current?.click()}
                className="inline-flex items-center gap-1 rounded border border-border bg-background px-2 py-1 text-xs font-semibold hover:bg-secondary"
              >
                <Upload className="h-3.5 w-3.5" /> Ersetzen
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={!clientId || uploading}
              className="flex w-full items-center justify-center gap-2 rounded border border-dashed border-border p-6 text-sm font-semibold text-muted-foreground hover:bg-secondary disabled:opacity-60"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Hochladen …
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />{" "}
                  {clientId ? "PDF auswählen" : "Erst Kunden wählen"}
                </>
              )}
            </button>
          )}
          <input
            ref={fileInput}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleUpload(f);
            }}
          />
          <p className="mt-1 text-[11px] text-muted-foreground">
            Maximal {formatBytes(MAX_BYTES)}. Datei bleibt privat, Zugriff nur
            über signierte URLs.
          </p>
        </div>

        <label className="flex items-start gap-2 rounded border border-border p-3 text-sm">
          <input
            type="checkbox"
            checked={visible}
            onChange={(e) => setVisible(e.target.checked)}
            className="mt-1"
          />
          <span>
            <span className="font-semibold">Für Kunde im Cockpit sichtbar</span>
            <span className="mt-0.5 block text-xs text-muted-foreground">
              Rechnungen sind standardmäßig sichtbar. Deaktivieren, wenn die
              Rechnung dem Kunden nicht angezeigt werden soll.
            </span>
          </span>
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Notizen (intern)
          </span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            className="w-full rounded-md border border-input bg-background p-2 text-sm"
            placeholder="Optionale Notizen – nur intern sichtbar."
          />
        </label>

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onSave}
            disabled={!canSave || saving || uploading}
            className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground disabled:opacity-60"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {isNew ? "Anlegen" : "Speichern"}
          </button>
          <Link
            to="/admin/invoices"
            className="text-sm font-semibold text-muted-foreground hover:underline"
          >
            Abbrechen
          </Link>
        </div>
      </div>
    </div>
  );
}
